import React, { useEffect, useState, Fragment } from 'react';
import { TruckIcon, PlusCircleIcon, MinusCircleIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, ClockIcon } from 'lucide-react';
interface Broker {
  id: string;
  name: string;
  company: string;
  rating: number;
  truckCount: number;
  isDefault?: boolean;
  rates: {
    [key: string]: number;
  };
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  loadLocation: string;
  dumpLocation: string;
  truckType?: string;
  travelTime?: string;
  assignments?: {
    brokerId: string;
    truckCount: number;
  }[];
}
interface AssignJobsToBrokersProps {
  jobs: Job[];
  brokers: Broker[];
  onAssignJobs: (jobId: string, assignments: {
    brokerId: string;
    truckCount: number;
  }[]) => void;
}
export const AssignJobsToBrokers = ({
  jobs,
  brokers,
  onAssignJobs
}: AssignJobsToBrokersProps) => {
  const [assignments, setAssignments] = useState<Record<string, {
    brokerId: string;
    truckCount: number;
  }[]>>({});
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  // Initialize assignments with default broker if not already set
  useEffect(() => {
    const newAssignments = {
      ...assignments
    };
    let updated = false;
    jobs.forEach(job => {
      if (!newAssignments[job.id] && !job.assignments) {
        const defaultBroker = brokers.find(broker => broker.isDefault);
        if (defaultBroker) {
          const truckCount = parseInt(job.numberOfTrucks.split(' ')[0]) || 1;
          newAssignments[job.id] = [{
            brokerId: defaultBroker.id,
            truckCount
          }];
          updated = true;
        }
      } else if (job.assignments && !newAssignments[job.id]) {
        newAssignments[job.id] = job.assignments;
        updated = true;
      }
    });
    if (updated) {
      setAssignments(newAssignments);
    }
  }, [jobs, brokers]);
  const handleAddBroker = (jobId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      // Find a broker that's not already assigned
      const assignedBrokerIds = currentAssignments.map(a => a.brokerId);
      const availableBroker = brokers.find(b => !assignedBrokerIds.includes(b.id));
      if (availableBroker) {
        return {
          ...prev,
          [jobId]: [...currentAssignments, {
            brokerId: availableBroker.id,
            truckCount: 1
          }]
        };
      }
      return prev;
    });
  };
  const handleRemoveBroker = (jobId: string, index: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments.splice(index, 1);
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeBroker = (jobId: string, index: number, brokerId: string) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        brokerId
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const handleChangeTruckCount = (jobId: string, index: number, truckCount: number) => {
    setAssignments(prev => {
      const currentAssignments = [...(prev[jobId] || [])];
      currentAssignments[index] = {
        ...currentAssignments[index],
        truckCount
      };
      return {
        ...prev,
        [jobId]: currentAssignments
      };
    });
  };
  const getTotalAssignedTrucks = (jobId: string) => {
    return (assignments[jobId] || []).reduce((sum, assignment) => sum + assignment.truckCount, 0);
  };
  const getRequiredTrucks = (job: Job) => {
    return parseInt(job.numberOfTrucks.split(' ')[0]) || 0;
  };
  const isJobFullyAssigned = (job: Job) => {
    return getTotalAssignedTrucks(job.id) === getRequiredTrucks(job);
  };
  const handleAssignJob = (jobId: string) => {
    onAssignJobs(jobId, assignments[jobId] || []);
  };
  return <div className="bg-white rounded-md shadow p-6" data-id="element-1080">
      <h2 className="text-xl font-bold mb-4" data-id="element-1081">Assign Jobs to Brokers</h2>
      {jobs.length === 0 ? <div className="bg-gray-50 p-6 rounded-md text-center" data-id="element-1082">
          <TruckIcon size={40} className="mx-auto mb-2 text-gray-400" data-id="element-1083" />
          <p className="text-gray-600" data-id="element-1084">No jobs available for assignment</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-1085">
            Create a new job to get started
          </p>
        </div> : <div className="overflow-x-auto" data-id="element-1086">
          <table className="w-full border-collapse" data-id="element-1087">
            <thead data-id="element-1088">
              <tr className="bg-gray-50" data-id="element-1089">
                <th className="text-left p-3 border-b" data-id="element-1090">Job Name</th>
                <th className="text-left p-3 border-b" data-id="element-1091">Job #</th>
                <th className="text-left p-3 border-b" data-id="element-1092">Date</th>
                <th className="text-left p-3 border-b" data-id="element-1093">Material</th>
                <th className="text-left p-3 border-b" data-id="element-1094">Truck Type</th>
                <th className="text-left p-3 border-b" data-id="element-1095">Trucks Needed</th>
                <th className="text-left p-3 border-b" data-id="element-1096">Travel Time</th>
                <th className="text-left p-3 border-b" data-id="element-1097">Location</th>
                <th className="text-left p-3 border-b" data-id="element-1098">Assignment Status</th>
                <th className="text-left p-3 border-b" data-id="element-1099">Actions</th>
              </tr>
            </thead>
            <tbody data-id="element-1100">
              {jobs.map(job => <Fragment key={job.id} data-id="element-1101">
                  <tr className="hover:bg-gray-50" data-id="element-1102">
                    <td className="p-3 border-b" data-id="element-1103">{job.jobName}</td>
                    <td className="p-3 border-b" data-id="element-1104">{job.jobNumber}</td>
                    <td className="p-3 border-b" data-id="element-1105">{job.date}</td>
                    <td className="p-3 border-b" data-id="element-1106">{job.material}</td>
                    <td className="p-3 border-b" data-id="element-1107">
                      <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full" data-id="element-1108">
                        {job.truckType || 'Not specified'}
                      </span>
                    </td>
                    <td className="p-3 border-b" data-id="element-1109">{job.numberOfTrucks}</td>
                    <td className="p-3 border-b" data-id="element-1110">
                      {job.travelTime ? <span className="flex items-center gap-1 text-sm" data-id="element-1111">
                          <ClockIcon size={14} className="text-gray-500" data-id="element-1112" />
                          <span className="font-medium text-orange-600" data-id="element-1113">
                            {job.travelTime} min
                          </span>
                        </span> : <span className="text-gray-400 text-sm" data-id="element-1114">
                          Not specified
                        </span>}
                    </td>
                    <td className="p-3 border-b" data-id="element-1115">
                      {job.loadLocation} → {job.dumpLocation}
                    </td>
                    <td className="p-3 border-b" data-id="element-1116">
                      {isJobFullyAssigned(job) ? <span className="flex items-center text-green-600" data-id="element-1117">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-1118" />
                          Fully Assigned
                        </span> : <span className="flex items-center text-orange-500" data-id="element-1119">
                          <AlertCircleIcon size={16} className="mr-1" data-id="element-1120" />
                          {getTotalAssignedTrucks(job.id)}/
                          {getRequiredTrucks(job)} Trucks Assigned
                        </span>}
                    </td>
                    <td className="p-3 border-b" data-id="element-1121">
                      <div className="flex gap-2" data-id="element-1122">
                        <button className="text-blue-600 hover:text-blue-800" onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)} data-id="element-1123">
                          {expandedJob === job.id ? 'Hide' : 'Assign'}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedJob === job.id && <tr data-id="element-1124">
                      <td colSpan={9} className="p-0 border-b" data-id="element-1125">
                        <div className="bg-gray-50 p-4" data-id="element-1126">
                          <div className="mb-4 flex items-center justify-between" data-id="element-1127">
                            <h3 className="font-medium" data-id="element-1128">Broker Assignments</h3>
                            <div className="flex items-center gap-2" data-id="element-1129">
                              <span className="text-sm text-gray-600" data-id="element-1130">
                                {getTotalAssignedTrucks(job.id)}/
                                {getRequiredTrucks(job)} trucks assigned
                              </span>
                              <button className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm" onClick={() => handleAddBroker(job.id)} disabled={(assignments[job.id] || []).length >= brokers.length} data-id="element-1131">
                                <PlusCircleIcon size={16} data-id="element-1132" />
                                Add Broker
                              </button>
                            </div>
                          </div>
                          {(assignments[job.id] || []).map((assignment, index) => <div key={index} className="bg-white p-3 rounded-md mb-2 border" data-id="element-1133">
                                <div className="flex items-center gap-4" data-id="element-1134">
                                  <div className="flex-grow" data-id="element-1135">
                                    <label className="block text-sm font-medium mb-1" data-id="element-1136">
                                      Broker
                                    </label>
                                    <select value={assignment.brokerId} onChange={e => handleChangeBroker(job.id, index, e.target.value)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-1137">
                                      {brokers.map(broker => {
                            // Check if this broker is already selected in another assignment
                            const isAlreadySelected = assignments[job.id]?.some((a, i) => a.brokerId === broker.id && i !== index);
                            // Get the rate for this job's truck type
                            const jobTruckType = job.truckType || '';
                            const rate = broker.rates?.[jobTruckType] || broker.rates?.['default'] || 0;
                            return <option key={broker.id} value={broker.id} disabled={isAlreadySelected} data-id="element-1138">
                                            {broker.name} ({broker.company}) - $
                                            {rate.toFixed(2)}/hr
                                          </option>;
                          })}
                                    </select>
                                  </div>
                                  <div className="w-48" data-id="element-1139">
                                    <label className="block text-sm font-medium mb-1" data-id="element-1140">
                                      Number of Trucks
                                    </label>
                                    <input type="number" min="1" max={getRequiredTrucks(job) - getTotalAssignedTrucks(job.id) + assignment.truckCount} value={assignment.truckCount} onChange={e => handleChangeTruckCount(job.id, index, parseInt(e.target.value) || 0)} className="w-full border border-gray-300 rounded-md p-2" data-id="element-1141" />
                                  </div>
                                  <div className="flex items-end" data-id="element-1142">
                                    <button className="p-2 text-red-500 hover:text-red-700" onClick={() => handleRemoveBroker(job.id, index)} data-id="element-1143">
                                      <MinusCircleIcon size={20} data-id="element-1144" />
                                    </button>
                                  </div>
                                </div>
                              </div>)}
                          {isJobFullyAssigned(job) ? <div className="mt-4 flex justify-end" data-id="element-1145">
                              <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center gap-2" onClick={() => handleAssignJob(job.id)} data-id="element-1146">
                                <TruckIcon size={16} data-id="element-1147" />
                                Send Job to Brokers
                              </button>
                            </div> : <div className="mt-4 bg-blue-50 p-3 rounded-md flex items-start gap-2" data-id="element-1148">
                              <InfoIcon size={18} className="text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1149" />
                              <div className="text-sm text-blue-800" data-id="element-1150">
                                <p data-id="element-1151">
                                  Please assign all {getRequiredTrucks(job)}{' '}
                                  trucks before sending the job.
                                </p>
                                <p className="mt-1" data-id="element-1152">
                                  You can split the job among multiple brokers
                                  or assign it to your internal fleet.
                                </p>
                              </div>
                            </div>}
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}
    </div>;
};